import type { AppRole } from "./enums.js";
import type { TableName } from "./entities.js";
import {
  ADMIN_ROLES,
  ATTENDANCE_WRITE_ROLES,
  BANK_READ_ROLES,
  EDIT_ROLES,
  PAYROLL_APPROVE_ROLES,
  PAYROLL_READ_ROLES,
  PAYROLL_WRITE_ROLES,
  VIRRGIN_ROLES,
  WORK_LOG_ROLES,
} from "./roles.js";

/**
 * Who may read a table.
 *  - "staff"  → any user with at least one assigned role (the old `private.is_staff()`)
 *  - "self"   → the record's own owner, or one of `readRoles`
 *  - role list → only those roles
 */
export type ReadScope = "staff" | "self" | "roles";

export interface TablePolicy {
  /** Logical table name, as the UI refers to it. */
  name: TableName;
  /** Read rule, transcribed from the `*_select` RLS policies. */
  read: ReadScope;
  /** Roles allowed to read when `read` is "roles", or to read others' rows when "self". */
  readRoles: AppRole[];
  /** Roles allowed to insert/update/delete, from the `*_write` RLS policies. */
  writeRoles: AppRole[];
  /** Roles allowed to delete, when narrower than `writeRoles`. */
  deleteRoles?: AppRole[];
  /** Column holding the owning user id, for `read: "self"`. */
  ownerColumn?: string;
  /** Natural key columns — enforced on write, and used to resolve upserts. */
  unique?: string[][];
  /** Columns that index a parent record; each becomes a GSI partition value. */
  parents?: string[];
  /**
   * A key that is not a bare `id`.
   *
   * Every other table here is partitioned on `id` with no sort key, which is what
   * lets one generic repository serve all of them. A table that declares this one
   * opts out of that repository — `findById`, `update` and `remove` cannot address
   * half a key — and gets its own access module instead. Only declare it where the
   * range query it buys is the point of the table.
   */
  key?: { partition: string; sort: string };
  /**
   * Local secondary indexes: the same partition key, an alternative sort.
   *
   * DynamoDB only accepts these when the table is *created*. They can never be
   * added to or removed from a live table, so declaring one is a one-shot
   * decision — and every write to the table also writes the index.
   */
  localIndexes?: { name: string; sortKey: string }[];
  /**
   * Global secondary indexes that need their own sort key, rather than the `id`
   * that `parents` pairs every partition with.
   */
  indexes?: { name: string; partitionKey: string; sortKey?: string }[];
  /** Default sort applied when the caller asks for none. */
  defaultOrder?: { column: string; ascending: boolean };
  /** Columns stored as numbers (DynamoDB N) rather than strings. */
  numeric?: string[];
  /** This table has no `updated_at` column. */
  noUpdatedAt?: boolean;
  /**
   * Values stamped on read when the stored item has no such attribute, so a
   * column added after rows were written still reads as the shape the pages
   * expect — without rewriting every existing item.
   */
  readDefaults?: Record<string, string | number | boolean>;
}

const TABLE_LIST: TablePolicy[] = [
  {
    name: "user_roles",
    read: "self",
    readRoles: ["super_admin"],
    writeRoles: ["super_admin"],
    ownerColumn: "user_id",
    unique: [["user_id", "role"]],
    parents: ["user_id"],
    noUpdatedAt: true,
  },
  {
    name: "countries",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["code"]],
    defaultOrder: { column: "name", ascending: true },
  },
  {
    name: "client_groups",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    // A group names the schedule it reads. Indexed so "which groups does this
    // schedule price" is a query rather than a scan — the cascade that keeps the
    // schedule's copy of their names honest asks it on every rename and delete.
    parents: ["fee_schedule_id"],
    defaultOrder: { column: "name", ascending: true },
  },
  {
    name: "companies",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    parents: ["client_group_id", "fee_schedule_id"],
    numeric: ["afr_outstanding_count"],
    // Added after rows were written, so a company nothing has restamped yet
    // reads as owing nothing rather than as `undefined` — which the register
    // would sort to the top as though it were the most urgent.
    readDefaults: { afr_outstanding_count: 0 },
    defaultOrder: { column: "legal_name", ascending: true },
  },
  {
    /*
     * One row per company per financial year — the AFR history.
     *
     * `period_end` is the sort key of every view of this table and the natural
     * key's second half, because it is the one date that identifies a financial
     * year unambiguously. The due date cannot: two companies with different
     * year-ends can share a due date, and a period granted an extension changes
     * its due date without becoming a different year.
     *
     * The `by_status` index is what the cross-company reads are built on — the
     * dashboard's "due in the next N days" and the AFR register's year view both
     * ask for open periods in due-date order, which is this index exactly. One
     * partition per status is three hot partitions in principle; at one row per
     * company per year it is nowhere near a throughput that would notice.
     */
    name: "company_afr_filings",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["company_id", "period_end"]],
    parents: ["company_id"],
    indexes: [{ name: "by_status", partitionKey: "status", sortKey: "afr_due_date" }],
    defaultOrder: { column: "period_end", ascending: false },
  },
  {
    /*
     * The kinds of document an AFR can carry — reference data, like `countries`.
     *
     * A table rather than an enum so a registry asking for something nobody
     * anticipated is a row somebody adds, not a deploy.
     */
    name: "afr_document_kinds",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["name"]],
    defaultOrder: { column: "name", ascending: true },
  },
  {
    /*
     * The files behind one financial year's return.
     *
     * `company_id` is a parent as well as `afr_filing_id`, because both
     * questions get asked: "what is attached to this year" on the year's own
     * row, and "every document we hold for this company" on the company. The
     * second would otherwise be a query per financial year.
     */
    name: "company_afr_filing_documents",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    parents: ["afr_filing_id", "company_id"],
    numeric: ["size_bytes"],
    defaultOrder: { column: "created_at", ascending: false },
  },
  {
    name: "contacts",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    // `company_id` is indexed as well as stored, because a company being
    // renamed or deleted has to reach the contacts attached to it.
    parents: ["client_group_id", "company_id"],
    defaultOrder: { column: "full_name", ascending: true },
  },
  {
    name: "company_contacts",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["company_id", "contact_id"]],
    parents: ["company_id", "contact_id"],
    noUpdatedAt: true,
  },
  {
    name: "contact_roles",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    parents: ["contact_id", "company_id", "client_group_id"],
  },
  {
    name: "service_categories",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["code"]],
    numeric: ["sort_order"],
    defaultOrder: { column: "sort_order", ascending: true },
  },
  {
    name: "services",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["code"]],
    parents: ["category_id"],
    numeric: ["default_fee"],
    defaultOrder: { column: "code", ascending: true },
  },
  {
    name: "service_bundles",
    read: "staff",
    readRoles: [],
    // Master data, so `EDIT_ROLES` rather than `WORK_LOG_ROLES`: everyone who
    // logs work may expand a bundle, but defining one is curating the catalogue.
    // The team roles that record work deliberately cannot mint them.
    writeRoles: EDIT_ROLES,
    unique: [["code"]],
    numeric: ["sort_order"],
    defaultOrder: { column: "sort_order", ascending: true },
  },
  {
    name: "service_bundle_items",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    // `service_id` is indexed so marking a service inactive can name the bundles that
    // still call for it, rather than leaving them to expand into a dead line.
    parents: ["bundle_id", "service_id"],
    numeric: ["quantity", "fee", "sort_order"],
    // No `unique` on (bundle_id, service_id), though that is the natural key:
    // `replaceByParent` swaps a bundle's whole set in one transaction and cannot
    // do that on a table with unique guards. The handler refuses duplicates
    // instead — the same trade `contact_roles` makes.
  },
  {
    name: "fee_schedules",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    // A schedule no longer names a client group or a company: only the target
    // names the schedule. `by_company_id` went with the scope columns.
    //
    // `client_group_id` is listed only to keep its index alive for one more
    // deploy — DynamoDB permits one global secondary index to be created *or*
    // deleted per table update, so dropping both at once is rejected outright.
    // Delete this line in the next deploy.
    parents: ["client_group_id"],
    // Rows written before `/fee-schedules` defaulted the column read as having
    // no `active` at all, and the two ends disagreed about what that meant: the
    // price resolver billed them (anything but `false` applies), while the page
    // drew them as switched off. Stamping the value they were written under
    // settles it — a schedule nobody marked inactive is active.
    //
    // `is_standard` reads false for the same reason: a schedule written before the
    // flag is not the catalogue's list until the seeder's backfill says which one is,
    // and "absent" has to mean one thing rather than being each reader's guess.
    readDefaults: { active: true, is_standard: false },
    defaultOrder: { column: "name", ascending: true },
  },
  {
    name: "fee_schedule_prices",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    // One partition per schedule, ordered by the date a price takes effect and
    // then by service. That is what makes "the prices in force on this date" a
    // range query and "this exact price" a single read — and it makes
    // (schedule, date, service) unique by construction, so no guard row is needed.
    key: { partition: "fee_schedule_id", sort: "sk" },
    // Most recently changed first, for a schedule's own audit view. Local rather
    // than global because the question is always asked within one schedule — and
    // it has to be declared now, since an LSI cannot be added to a live table.
    localIndexes: [{ name: "by_updated_at", sortKey: "updated_at" }],
    // The cascade read backwards: every schedule that prices one service. Sorted
    // by the same key, so a service's prices arrive grouped by schedule and date.
    indexes: [{ name: "by_service_id", partitionKey: "service_id", sortKey: "sk" }],
    numeric: ["standard_fee", "special_fee", "min_fee", "max_fee", "discount_pct", "markup_pct"],
    readDefaults: { active: true },
  },
  {
    name: "company_services",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["company_id", "service_id"]],
    parents: ["company_id", "service_id", "billing_contact_id"],
    numeric: ["fee"],
  },
  {
    name: "invoice_fee_overrides",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    deleteRoles: ["super_admin"],
    parents: ["company_id", "service_id", "company_service_id"],
    numeric: ["original_fee", "revised_fee"],
    noUpdatedAt: true,
  },
  {
    name: "bank_accounts",
    read: "roles",
    readRoles: BANK_READ_ROLES,
    writeRoles: EDIT_ROLES,
    defaultOrder: { column: "account_name", ascending: true },
  },
  {
    // One row. Read wherever the bank details are read, since the invoice's
    // payment block is assembled from both.
    name: "remittance_defaults",
    read: "roles",
    readRoles: BANK_READ_ROLES,
    writeRoles: EDIT_ROLES,
    defaultOrder: { column: "created_at", ascending: true },
  },
  {
    // One row; absent reads as the defaults on `InvoiceSettings`.
    name: "invoice_settings",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    numeric: ["bulk_link_ttl_hours"],
    defaultOrder: { column: "created_at", ascending: true },
  },
  {
    // Written by the server only: a run is started through `POST /invoices/runs`
    // and filled in by its worker, never edited row by row.
    name: "invoice_runs",
    read: "roles",
    readRoles: EDIT_ROLES,
    writeRoles: [],
    defaultOrder: { column: "created_at", ascending: false },
  },
  {
    // Written by the server only, as each kind of bulk action starts and
    // finishes; the Bulk actions page reads it and nothing edits it.
    name: "bulk_actions",
    read: "roles",
    readRoles: EDIT_ROLES,
    writeRoles: [],
    defaultOrder: { column: "created_at", ascending: false },
  },
  {
    name: "invoice_notes",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    numeric: ["sort_order"],
    // A note reaches an invoice by being standard wording or by being attached
    // to the company, the client group or the account — those two things and
    // nothing else. So a row that never said which is neither: `false` here, not
    // `true`.
    //
    // It was `true` for one release, to preserve the behaviour those rows were
    // written under, where every active note was ticked. That turned out to be
    // the wrong half to preserve: the notes written before the column are mostly
    // per-account wire instructions, and reading them as standard put every
    // bank's instructions on every invoice — which is the thing the attachment
    // sets exist to stop. The attachment columns need no default at all: absent
    // reads as "names nobody", which is what they are.
    readDefaults: { is_standard: false },
    defaultOrder: { column: "sort_order", ascending: true },
  },
  {
    name: "invoices",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["invoice_number"]],
    parents: ["company_id", "client_group_id", "bill_to_contact_id", "bank_account_id"],
    numeric: ["subtotal", "total"],
    defaultOrder: { column: "invoice_date", ascending: false },
  },
  {
    name: "invoice_lines",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    parents: ["invoice_id", "service_id"],
    numeric: ["line_no", "quantity", "unit_amount", "amount"],
    defaultOrder: { column: "line_no", ascending: true },
    noUpdatedAt: true,
  },
  {
    name: "service_events",
    read: "staff",
    readRoles: [],
    writeRoles: WORK_LOG_ROLES,
    // `bundle_id` is deliberately *not* a parent. Nothing asks "what was logged
    // from this bundle" — the stamp is read off the rows that come back, not
    // queried by — and a GSI would make the id an attribute DynamoDB indexes, so
    // the common case of a row with no bundle behind it would have to drop it.
    parents: ["company_id", "service_id", "invoice_id"],
    numeric: ["quantity", "amount"],
    // No `readDefaults` for the bundle stamp. It arrived after rows were already
    // written, and an entry that predates it simply has no such attribute —
    // which is what "logged from no bundle" means, and what every reader of it
    // already treats as the answer.
    defaultOrder: { column: "event_date", ascending: false },
  },
  {
    name: "sundry_expenses",
    read: "staff",
    readRoles: [],
    writeRoles: WORK_LOG_ROLES,
    parents: ["company_id", "invoice_id"],
    numeric: ["amount", "markup_pct"],
    defaultOrder: { column: "expense_date", ascending: false },
  },
  {
    name: "virrgin_transactions",
    read: "staff",
    readRoles: [],
    writeRoles: VIRRGIN_ROLES,
    unique: [["row_hash"]],
    parents: ["company_id", "invoice_id"],
    numeric: ["amount"],
    // Rows written before the float had deposits are all drawdowns.
    readDefaults: { txn_type: "charge" },
    defaultOrder: { column: "txn_date", ascending: false },
  },
  {
    name: "audit_logs",
    read: "roles",
    // `audit_select_admin`
    readRoles: ["super_admin", "somasi_owner", "outsourced_admin"],
    // The old policy dropped client-side inserts; only server workers write here.
    writeRoles: [],
    parents: ["user_id", "entity_id"],
    defaultOrder: { column: "created_at", ascending: false },
    noUpdatedAt: true,
  },

  // ---- Payroll, leave and attendance ---------------------------------------
  {
    name: "employees",
    /*
     * The person, and nothing that is nobody else's business. A name, a job
     * title, a manager, the days somebody works — a colleague reading the
     * attendance grid needs all of it, so this table reads as `"staff"`.
     *
     * It was `PAYROLL_READ_ROLES` while the salary sat on this row, and four
     * handlers projected names server-side to work around that. The contract
     * moved to `employee_contracts`, which carries the restriction instead, and
     * the projections went with it.
     */
    read: "staff",
    readRoles: [],
    writeRoles: PAYROLL_WRITE_ROLES,
    unique: [["employee_no"]],
    parents: ["user_id", "manager_employee_id"],
    defaultOrder: { column: "full_name", ascending: true },
  },
  {
    name: "employee_contracts",
    /*
     * What the person is paid, registered as, and paid into — the half of an
     * employee that only the payroll may read. One row per employee, keyed on
     * the employee's own `id`: one contract each by construction, addressable
     * by the generic repository, and no index to keep honest.
     *
     * Not written from a browser directly. `PATCH /employees/{id}` splits a
     * form's payload across both rows, and `/employees/{id}/contract` is the
     * endpoint for this half on its own.
     */
    read: "roles",
    readRoles: PAYROLL_READ_ROLES,
    writeRoles: PAYROLL_WRITE_ROLES,
    numeric: [
      "annual_gross",
      "opening_ytd_year",
      "opening_ytd_gross",
      "opening_ytd_taxed",
      "opening_ytd_untaxed",
      "opening_ytd_tax_paid",
    ],
    /*
     * A contract written before a column existed reads as the shape the payroll
     * expects. `annual_gross` is deliberately absent from this list: a salary
     * defaulted to zero would run a payroll that paid somebody nothing, and the
     * pay run should refuse a contract that does not state one.
     */
    readDefaults: {
      pay_frequency: "monthly",
      payroll_tax_exempt: false,
      work_permit_required: false,
      /*
       * A contract written before the opening balances existed is a contract
       * for somebody the system has paid from the start, so zero is the honest
       * default: nothing was carried in.
       *
       * `opening_ytd_year` is absent from this list because a default cannot be
       * null, and null is what it has to be — an older row names no year, and
       * naming one would apply four zeroes to it. Reading it back as undefined
       * says the same thing, and the pay run treats the two alike.
       */
      opening_ytd_gross: 0,
      opening_ytd_taxed: 0,
      opening_ytd_untaxed: 0,
      opening_ytd_tax_paid: 0,
    },
  },
  {
    name: "employee_pay_items",
    read: "roles",
    readRoles: PAYROLL_READ_ROLES,
    writeRoles: PAYROLL_WRITE_ROLES,
    parents: ["employee_id"],
    numeric: ["amount", "balance_remaining"],
    defaultOrder: { column: "effective_from", ascending: false },
  },
  {
    name: "payroll_rates",
    // The rates themselves are public law, and every screen that previews a net
    // figure needs them. What they are applied to is the restricted part.
    read: "staff",
    readRoles: [],
    writeRoles: PAYROLL_WRITE_ROLES,
    numeric: [
      "payroll_tax_total_pct",
      "payroll_tax_employee_pct",
      "payroll_tax_annual_exemption",
      "nhi_employee_pct",
      "nhi_employer_pct",
      "nhi_period_cap",
      "ssb_employee_pct",
      "ssb_employer_pct",
      "ssb_period_cap",
      "weeks_per_year",
      "filing_due_days",
      "annual_filing_due_days",
      "self_time_amendments_per_month",
    ],
  },
  {
    name: "leave_types",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["code"]],
    parents: ["beyond_limit_type_id"],
    numeric: [
      "paid_percentage",
      "paid_days_limit",
      "default_allowance_days",
      "carry_over_max_days",
      "sort_order",
    ],
    defaultOrder: { column: "sort_order", ascending: true },
  },
  {
    name: "leave_allowances",
    // Who has how much leave left is ordinary operational information in an
    // office this size, and the booking screen needs it to refuse a request it
    // cannot honour.
    read: "staff",
    readRoles: [],
    writeRoles: PAYROLL_APPROVE_ROLES,
    unique: [["employee_id", "leave_type_id", "year"]],
    parents: ["employee_id", "leave_type_id"],
    numeric: ["year", "entitlement_days", "carried_in_days", "adjustment_days", "paid_days_limit"],
  },
  {
    name: "leave_requests",
    read: "staff",
    readRoles: [],
    writeRoles: ATTENDANCE_WRITE_ROLES,
    parents: ["employee_id", "leave_type_id"],
    numeric: ["total_halves"],
    defaultOrder: { column: "start_date", ascending: false },
  },
  {
    name: "wfh_requests",
    /*
     * `read: "staff"` with the same scoping the leave requests get: an employee
     * sees their own, a manager their team's, the payroll everybody's. The
     * handler narrows it — this is the outer bound.
     */
    read: "staff",
    readRoles: [],
    writeRoles: ATTENDANCE_WRITE_ROLES,
    parents: ["employee_id"],
    numeric: ["total_days"],
    defaultOrder: { column: "start_date", ascending: false },
  },
  {
    name: "attendance_days",
    read: "staff",
    readRoles: [],
    // Writes come through `/attendance`, which decides what a caller may do to
    // whose day — an employee declares their own, a manager settles their team's.
    // The role list here is the outer bound, not the whole rule.
    writeRoles: ATTENDANCE_WRITE_ROLES,
    /*
     * Every read of this table is "this employee, this month", which is a range
     * query over one partition — so it is keyed on the pair rather than on an
     * `id`, and brings its own access module instead of the generic repository.
     * It is the one table here where that trade is clearly worth it: a year of
     * attendance for five people is already 1,300 rows, and scanning them to
     * draw one month's grid would get worse every year.
     */
    key: { partition: "employee_id", sort: "date" },
    defaultOrder: { column: "date", ascending: true },
  },
  {
    name: "presence_signals",
    // A movement record about named people. Only the rollup writes it, only an
    // administrator can read it, and the rows expire on their own.
    read: "roles",
    readRoles: ADMIN_ROLES,
    writeRoles: [],
    key: { partition: "employee_id", sort: "window" },
    numeric: ["signal_count", "ttl"],
    noUpdatedAt: true,
  },
  {
    name: "employee_documents",
    /*
     * Somebody's own contract, permit and ID are theirs to see; everybody
     * else's are the payroll's. The same `read: "self"` device as `payslips`,
     * and for the same reason — a policy cannot join, so the login is copied
     * onto the row and the row answers for itself.
     */
    read: "self",
    readRoles: PAYROLL_READ_ROLES,
    ownerColumn: "user_id",
    writeRoles: PAYROLL_WRITE_ROLES,
    parents: ["employee_id", "user_id"],
    numeric: ["size_bytes"],
    defaultOrder: { column: "created_at", ascending: false },
  },
  {
    name: "time_entries",
    read: "staff",
    readRoles: [],
    // Clocking in is the employee's own act; `/attendance/clock-in` decides
    // whose entry a caller may touch, which is narrower than this outer bound.
    writeRoles: ATTENDANCE_WRITE_ROLES,
    /*
     * Keyed on the pair, like `attendance_days`: every read is "this employee,
     * this day" or "this employee, is anything still open". Several entries in
     * one day are ordinary — a lunch break, a site visit — so the start time is
     * the sort key rather than the date.
     */
    key: { partition: "employee_id", sort: "started_at" },
    numeric: ["minutes"],
    defaultOrder: { column: "started_at", ascending: false },
  },
  {
    name: "time_amendments",
    /*
     * `read: "staff"` with the handler doing the scoping, exactly as the leave
     * and work-from-home requests do: an employee sees their own corrections, a
     * manager their team's, the payroll everybody's. This is the outer bound.
     */
    read: "staff",
    readRoles: [],
    /*
     * Writes come through `/time-amendments`, which decides whose time a caller
     * may correct and whether the correction takes effect or waits for an admin.
     * There is no PATCH and no DELETE: a correction is decided, not edited, and
     * the row is the audit trail of who changed a clocked time.
     */
    writeRoles: ATTENDANCE_WRITE_ROLES,
    /*
     * No index on the employee. Every read here is "the corrections this caller
     * may see", which the handler scopes against the people they may see rather
     * than against one id — so an index would be a table-wide cost serving
     * nothing, on a stack already close to the resources CloudFormation allows.
     */
    defaultOrder: { column: "created_at", ascending: false },
  },
  {
    name: "office_networks",
    // Security configuration: knowing which addresses are trusted is most of
    // what you need to know to arrange to look like you are inside one.
    read: "roles",
    readRoles: ADMIN_ROLES,
    writeRoles: ADMIN_ROLES,
    defaultOrder: { column: "label", ascending: true },
  },
  {
    name: "holidays",
    read: "staff",
    readRoles: [],
    writeRoles: EDIT_ROLES,
    unique: [["territory", "date"]],
    defaultOrder: { column: "date", ascending: true },
  },
  {
    name: "pay_periods",
    read: "roles",
    readRoles: PAYROLL_READ_ROLES,
    writeRoles: PAYROLL_WRITE_ROLES,
    unique: [["year", "sequence"]],
    numeric: ["year", "sequence"],
    defaultOrder: { column: "period_start", ascending: false },
  },
  {
    name: "payslips",
    /*
     * An employee reads their own payslip and nobody else's; the payroll roles
     * read everybody's. `user_id` is copied onto the row for exactly this,
     * because a policy here cannot join through `employees` to find out whose
     * payslip this is.
     */
    read: "self",
    readRoles: PAYROLL_READ_ROLES,
    ownerColumn: "user_id",
    // Produced by the pay run inside `/pay-periods/.../calculate`, never posted
    // from a browser. A payslip anybody could write is not a record of anything.
    writeRoles: [],
    unique: [["pay_period_id", "employee_id"]],
    parents: ["pay_period_id", "employee_id", "user_id"],
    numeric: [
      "contracted_gross",
      "day_rate",
      "half_day_rate",
      "unpaid_half_days",
      "unpaid_deduction",
      "not_employed_half_days",
      "not_employed_deduction",
      "additions_total",
      "gross",
      "ytd_gross_before",
      "payroll_tax_exempt_applied",
      "payroll_tax_taxable",
      "payroll_tax_employee",
      "payroll_tax_employer",
      "nhi_base",
      "nhi_employee",
      "nhi_employer",
      "ssb_base",
      "ssb_employee",
      "ssb_employer",
      "deductions_total",
      "employee_deductions_total",
      "net",
      "employer_contributions_total",
      "employer_cost",
      "ytd_gross",
      "ytd_taxed",
      "ytd_untaxed",
      "ytd_tax_paid",
      "total_earnings",
    ],
  },
  {
    name: "payroll_filings",
    read: "roles",
    readRoles: PAYROLL_READ_ROLES,
    writeRoles: PAYROLL_WRITE_ROLES,
    unique: [["pay_period_id", "authority"]],
    parents: ["pay_period_id"],
    numeric: ["employee_total", "employer_total", "total"],
    defaultOrder: { column: "due_date", ascending: true },
  },
  {
    /*
     * The files behind a return. Read by whoever may read the return itself —
     * these are the business's own filings, not anybody's personal papers, so
     * there is no `self` row filter to apply as `employee_documents` has.
     */
    name: "payroll_filing_documents",
    read: "roles",
    readRoles: PAYROLL_READ_ROLES,
    writeRoles: PAYROLL_WRITE_ROLES,
    parents: ["payroll_filing_id"],
    numeric: ["size_bytes"],
    defaultOrder: { column: "created_at", ascending: false },
  },
];

export const TABLE_POLICIES: Record<TableName, TablePolicy> = Object.fromEntries(
  TABLE_LIST.map((t) => [t.name, t]),
) as Record<TableName, TablePolicy>;

export const TABLE_NAMES = TABLE_LIST.map((t) => t.name);

export function isTableName(value: string): value is TableName {
  return Object.prototype.hasOwnProperty.call(TABLE_POLICIES, value);
}
